import { Navigate } from 'react-router-dom';
import { Can } from 'context';
import { exportMetrics, getMetrics, getMetricGames, getMetricCountries } from 'services/metrics';
import metricColumnData from 'data/metricColumnData';
import { useEffect, useState, useMemo } from 'react';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import usePersistedFilters, { usePageFilters } from 'hooks/usePersistedFilters';
import UnifiedFilterBar from 'components/UnifiedFilterBar';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'components/DataTablePage/components/DataTable';

const DEFAULT_PAGE_FILTERS = {
  search: '',
  roundHash: '',
  gameType: '',
  country: ''
};

function Metrics() {
  // Shared filters (date, entity)
  const { filters: sharedFilters, setDateRange, setEntityFilters } = usePersistedFilters();

  // Page-specific filters
  const { pageFilters, setPageFilters, clearPageFilters } = usePageFilters('metrics', DEFAULT_PAGE_FILTERS);

  // Async options for game and country filters
  const [gameOptions, setGameOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  // Load game and country options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [gamesRes, countriesRes] = await Promise.all([getMetricGames(), getMetricCountries()]);

        if (gamesRes?.status === 200 || gamesRes?.status === 201) {
          const games = (gamesRes.data ?? []).map((name) => ({
            value: String(name),
            label: String(name)
          }));
          setGameOptions(games);
        }

        if (countriesRes?.status === 200 || countriesRes?.status === 201) {
          const countries = (countriesRes.data ?? []).map((name) => ({
            value: String(name),
            label: String(name)
          }));
          setCountryOptions(countries);
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };

    loadOptions();
  }, []);

  // Combined filters for API calls
  const combinedFilters = useMemo(
    () => ({
      startDate: sharedFilters.startDate ? sharedFilters.startDate.toISOString() : null,
      endDate: sharedFilters.endDate ? sharedFilters.endDate.toISOString() : null,
      clientId: sharedFilters.clientIds?.[0] || '',
      operatorId: sharedFilters.operatorIds?.[0] || '',
      brandId: sharedFilters.brandIds?.[0] || '',
      search: pageFilters.search,
      roundHash: pageFilters.roundHash,
      gameType: pageFilters.gameType,
      country: pageFilters.country
    }),
    [sharedFilters, pageFilters]
  );

  const queryKey = ['metrics', combinedFilters];

  // Page-specific filter configuration
  const pageFilterConfig = useMemo(
    () => [
      {
        key: 'gameType',
        label: 'Game',
        type: 'async-select',
        loadedOptions: gameOptions
      },
      {
        key: 'country',
        label: 'Country',
        type: 'async-select',
        loadedOptions: countryOptions
      },
      {
        key: 'search',
        label: 'Search',
        type: 'text'
      },
      {
        key: 'roundHash',
        label: 'Round Hash',
        type: 'text'
      }
    ],
    [gameOptions, countryOptions]
  );

  const handleExport = async () => {
    const data = {
      startDate: sharedFilters?.startDate ?? null,
      endDate: sharedFilters?.endDate ?? null,
      clientId: sharedFilters.clientIds?.[0] || undefined,
      operatorId: sharedFilters.operatorIds?.[0] || undefined,
      brandId: sharedFilters.brandIds?.[0] || undefined,
      gameType: pageFilters.gameType || undefined,
      country: pageFilters.country || undefined
    };

    try {
      const response = await exportMetrics(data);
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'metrics_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Filter Bar - Above the table */}
        <UnifiedFilterBar
          filters={sharedFilters}
          setDateRange={setDateRange}
          setEntityFilters={setEntityFilters}
          pageFilters={pageFilters}
          setPageFilters={setPageFilters}
          pageFilterConfig={pageFilterConfig}
          onClearPageFilters={clearPageFilters}
        />

        {/* Data Table */}
        <Card>
          <MDBox
            sx={{
              height: '3px',
              background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
              borderRadius: '12px 12px 0 0'
            }}
          />
          <MDBox p={3} lineHeight={1} display='flex' justifyContent='space-between' alignItems='center'>
            <MDBox display='flex' alignItems='center' gap={1.5}>
              <MDBox
                sx={{
                  width: 38, height: 38, borderRadius: '10px',
                  background: '#ce93d818', border: '1px solid #ce93d840',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}
              >
                <Icon sx={{ fontSize: '1.25rem', color: '#ce93d8' }}>bar_chart</Icon>
              </MDBox>
              <MDTypography variant='h5' fontWeight='medium'>
                Metrics
              </MDTypography>
            </MDBox>
            <MDButton variant='contained' color='info' onClick={handleExport}>
              Export
            </MDButton>
          </MDBox>
          <DataTable
            canFilter={false}
            fetchData={getMetrics}
            queryKey={queryKey}
            columnData={metricColumnData(() =>
              setPageFilters((prev) => ({ ...prev, refreshCounter: (prev.refreshCounter || 0) + 1 }))
            )}
            object='metric'
            noActions
            filters={combinedFilters}
          />
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Metrics;
